/**
 * StrikeHistoryPanel — read-only timeline of safety strikes/warnings the
 * member has received. Reads from `member_strikes` (RLS scoped to caller).
 * If the table doesn't exist or the member has none, renders nothing.
 */
import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { formatDistanceToNow } from "date-fns";

interface Strike {
  id: string;
  reason: string;
  severity: "warning" | "minor" | "major";
  created_at: string;
  expires_at: string | null;
  notes: string | null;
}

const SEVERITY_COLOR: Record<string, string> = {
  warning: "text-muted-foreground",
  minor: "text-amber-500",
  major: "text-destructive",
};

const StrikeHistoryPanel = () => {
  const { user } = useAuth();
  const [strikes, setStrikes] = useState<Strike[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        // The table is optional; fail silently if it doesn't exist.
        const { data } = await supabase
          .from("member_strikes" as never)
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });
        setStrikes((data ?? []) as Strike[]);
      } catch {
        setStrikes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (loading || strikes.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Safety history</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Warnings and strikes on your account. Most expire automatically.
      </p>
      <ul className="mt-4 space-y-2">
        {strikes.map((s) => (
          <li
            key={s.id}
            className="flex items-start justify-between gap-3 rounded-lg bg-muted/40 p-3 text-sm"
          >
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-medium uppercase tracking-wider ${SEVERITY_COLOR[s.severity] ?? ""}`}>
                {s.severity}
              </p>
              <p className="mt-0.5">{s.reason}</p>
              {s.notes && <p className="mt-1 text-xs text-muted-foreground">{s.notes}</p>}
            </div>
            <div className="shrink-0 text-right text-[11px] text-muted-foreground">
              {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}
              {s.expires_at && (
                <p className="mt-0.5">
                  expires {formatDistanceToNow(new Date(s.expires_at), { addSuffix: true })}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default StrikeHistoryPanel;
